import { useEffect } from "react";
import { create } from "zustand";

// The default page title lives in PageContentHeaderZone, which renders above
// the page content and cannot read page state directly. A page that wants the
// title hidden for part of its lifetime (e.g. a full-bleed zero state that
// carries its own heading) calls `useHideDefaultPageTitle` while that content
// is mounted. In-memory only; nothing here is persisted.
//
// `hiddenCount` is a count, not a boolean, so two overlapping callers cannot
// unhide the title while the other still wants it hidden.
type State = {
  hiddenCount: number;
  hide: () => void;
  show: () => void;
};

const useStore = create<State>((set) => ({
  hiddenCount: 0,
  hide: () => set((state) => ({ hiddenCount: state.hiddenCount + 1 })),
  show: () =>
    set((state) => ({ hiddenCount: Math.max(0, state.hiddenCount - 1) })),
}));

/** True while any mounted component has asked for the title to be hidden. */
export const useDefaultPageTitleHidden = (): boolean =>
  useStore((state) => state.hiddenCount > 0);

/** Hides the default page title while `hidden` is true and this stays mounted. */
export const useHideDefaultPageTitle = (hidden = true): void => {
  useEffect(() => {
    if (!hidden) return;
    const { hide, show } = useStore.getState();
    hide();
    return show;
  }, [hidden]);
};
